import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchMachineRepairReport } from '../../features/machines/machinesSlice';
import { commonTextAreaStyles } from '../../utils/textFieldStyles';
import DragDropFileUpload from '../DragDropFileUpload';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';

import {
  TextField,
  Typography,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ModalButton, ModalDialogActions } from '../ModalComponents';

import styles from './styles.module.css';
import 'dayjs/locale/de';

const initialForm = {
  comments: '',
};

export default function ModalReportRepair({ id, machineName, machineNumber, groupName, groupImg }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [reportDay, setReportDay] = useState(dayjs());
  const [isDirty, setIsDirty] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => {
    setOpen(true);
  };

  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    setSelectedFile(null);
    setIsDirty(false);
  };
  const handleClose = () => guardClose(doClose);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (date) => {
    setIsDirty(true);
    setReportDay(date);
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('comment', form.comments);
      formData.append('machine', JSON.stringify({ id }));
      formData.append('file', selectedFile);
      const formattedDate = dayjs(reportDay).format('YYYY-MM-DDTHH:mm:ssZ');
      formData.append('activatedAt', formattedDate);
      if (!form.comments.trim()) {
        setErrorResponse(t('modals.commentNotEmpty'));
        return;
      }
      const response = await dispatch(fetchMachineRepairReport(formData));
      if (response.payload?.error || response.payload?.statusCode === 500) {
        setErrorResponse(response.payload.message);
      } else {
        doClose();
      }
    } catch (err) {
      console.error('Error registering repair report:', err);
    }
  };

  return (
    <div style={{ alignSelf: 'flex-end' }}>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        sx={{
          textTransform: 'none',
          padding: '12px 24px',
          alignSelf: 'flex-end',
          color: 'white',
          '& .MuiTypography-root': {
            color: 'white',
          },
        }}
      >
        {t('modals.reportRepair')}
      </Button>
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={{ m: 0, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <img
                src={`${process.env.REACT_APP_BASE_URL}machine-groups/image/${groupImg}`}
                alt=""
                style={{ maxWidth: '70px', maxHeight: '50px', borderRadius: '4px' }}
              />
              <Box>
                <Typography variant="subtitle1" color="text.secondary">
                  {groupName}
                </Typography>
                <Typography variant="h6" fontWeight="500">
                  {machineNumber} {machineName}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent
            dividers
            sx={{
              p: 3,
              maxHeight: '60vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'var(--scrollbar-track)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--scrollbar-thumb)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'var(--scrollbar-thumb-hover)',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, opacity: 0.7 }}>
                  {t('modals.unscheduled')}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Task Description:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('modals.pleaseProvide')}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={t('modals.comments')}
                  variant="outlined"
                  value={form.comments}
                  name="comments"
                  onChange={handleChange}
                  multiline
                  rows={3}
                  fullWidth
                  sx={commonTextAreaStyles}
                />

                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                  <DatePicker
                    label={t('modals.reportDay')}
                    onChange={handleDateChange}
                    fullWidth
                    maxDate={dayjs(Date.now())}
                    value={reportDay}
                  />
                </LocalizationProvider>

                <DragDropFileUpload
                  accept="/*"
                  selectedFile={selectedFile}
                  onFileChange={(file, error) => {
                    setIsDirty(true);
                    if (error) {
                      setErrorResponse(error);
                      setSelectedFile(null);
                    } else {
                      setErrorResponse('');
                      setSelectedFile(file);
                    }
                  }}
                  onFileRemove={() => {
                    setSelectedFile(null);
                    setErrorResponse('');
                  }}
                  placeholder={t('modals.uploadFile')}
                />
                {errorResponse &&
                  (Array.isArray(errorResponse) ? (
                    errorResponse.map((item, index) => (
                      <Typography key={index} color="error">
                        {item}
                      </Typography>
                    ))
                  ) : (
                    <Typography color="error">{errorResponse}</Typography>
                  ))}
              </Box>
            </Box>
          </DialogContent>
          <ModalDialogActions>
            <ModalButton variant="outlined" color="primary" onClick={handleClose}>
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton variant="contained" color="primary" onClick={handleSubmit}>
              {t('modals.report')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
